import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Pause } from 'lucide-react';
import axiosInstance from '@/lib/axios';
import { getAdVisitorId } from '@/lib/ad-visitor';
import { cn } from '@/lib/utils';
import type { AdvertisementCampaign } from '@/types';

const AUTO_ADVANCE_MS = 5000;
const SWIPE_CONFIDENCE_THRESHOLD = 80;

interface AdvertisementBannerCarouselProps {
  communityId: string;
  campaigns: AdvertisementCampaign[];
  className?: string;
}

export function AdvertisementBannerCarousel({
  communityId,
  campaigns,
  className,
}: AdvertisementBannerCarouselProps) {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState<1 | -1>(1);
  const [isHoverOrFocusPaused, setIsHoverOrFocusPaused] = useState(false);
  const [isHoldPaused, setIsHoldPaused] = useState(false);
  const dragMetaRef = useRef<{ startedAt: number; dragPx: number }>({ startedAt: 0, dragPx: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  const count = campaigns.length;
  const campaign = campaigns[index];
  const campaignIds = useMemo(() => campaigns.map((c) => c.id).join(','), [campaigns]);
  const isPaused = isHoldPaused || isHoverOrFocusPaused;

  const recordView = useCallback(
    (campaignId: string) => {
      const visitorId = getAdVisitorId();
      axiosInstance
        .post(`/api/communities/${communityId}/advertisement-campaigns/${campaignId}/view`, {
          visitorId: visitorId || undefined,
        })
        .catch(console.error);
    },
    [communityId]
  );

  const recordClick = useCallback(
    (campaignId: string) => {
      const visitorId = getAdVisitorId();
      axiosInstance
        .post(`/api/communities/${communityId}/advertisement-campaigns/${campaignId}/click`, {
          visitorId: visitorId || undefined,
        })
        .catch(console.error);
    },
    [communityId]
  );

  useEffect(() => {
    const campaignId = campaign?.id;
    if (!campaignId) return;
    recordView(campaignId);
  }, [campaign?.id, recordView]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      setDirection(1);
      setIndex(0);
    }, 0);
    return () => window.clearTimeout(t);
  }, [campaignIds]);

  useEffect(() => {
    if (count <= 1 || isPaused) return;

    const timer = window.setInterval(() => {
      setDirection(1);
      setIndex((i) => (i + 1) % count);
    }, AUTO_ADVANCE_MS);

    return () => window.clearInterval(timer);
  }, [count, isPaused, index]);

  useEffect(() => {
    if (!isHoldPaused) return;

    const onPointerDown = (e: PointerEvent) => {
      const el = containerRef.current;
      if (!el) return;
      const target = e.target as Node | null;
      if (target && el.contains(target)) return;
      setIsHoldPaused(false);
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [isHoldPaused]);

  if (!campaign || count === 0) return null;

  const ad = campaign.advertisement;
  const goPrev = () => {
    setDirection(-1);
    setIndex((i) => (i - 1 + count) % count);
  };
  const goNext = () => {
    setDirection(1);
    setIndex((i) => (i + 1) % count);
  };

  const variants = {
    enter: (dir: 1 | -1) => ({ x: dir > 0 ? 40 : -40, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir: 1 | -1) => ({ x: dir > 0 ? -40 : 40, opacity: 0 }),
  } as const;

  return (
    <div className={cn('relative w-full', className)}>
      <div
        ref={containerRef}
        className="group relative overflow-hidden rounded-2xl border border-border/10 shadow-sm bg-muted/30 aspect-video md:max-h-[320px] lg:max-h-[360px] xl:max-h-[420px]"
        onMouseEnter={() => setIsHoverOrFocusPaused(true)}
        onMouseLeave={() => setIsHoverOrFocusPaused(false)}
        onFocusCapture={() => setIsHoverOrFocusPaused(true)}
        onContextMenu={(e) => e.preventDefault()}
        onBlurCapture={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setIsHoverOrFocusPaused(false);
          }
        }}
        onPointerDown={() => {
          setIsHoldPaused(true);
        }}
      >
        <AnimatePresence mode="wait" initial={false} custom={direction}>
          <motion.div
            key={campaign.id}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0"
            drag={count > 1 ? 'x' : false}
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.2}
            onDragStart={(_e, _info) => {
              dragMetaRef.current = { startedAt: Date.now(), dragPx: 0 };
              setIsHoldPaused(true);
            }}
            onDrag={(_e, info) => {
              dragMetaRef.current.dragPx = info.offset.x;
            }}
            onDragEnd={(_e, info) => {
              const swipe = info.offset.x;
              const velocity = info.velocity.x;
              const swipePower = Math.abs(swipe) * (Math.abs(velocity) / 500);

              dragMetaRef.current.dragPx = swipe;

              if (swipe < -SWIPE_CONFIDENCE_THRESHOLD || swipePower > 120) {
                goNext();
              } else if (swipe > SWIPE_CONFIDENCE_THRESHOLD || swipePower > 120) {
                goPrev();
              }
            }}
          >
            <a
              href={ad.redirectUrl || '#'}
              target={ad.redirectUrl ? '_blank' : '_self'}
              rel="noopener noreferrer"
              className="absolute inset-0 block"
              onClick={(e) => {
                const dragged = Math.abs(dragMetaRef.current.dragPx);
                if (dragged > 8) {
                  e.preventDefault();
                  e.stopPropagation();
                  return;
                }
                recordClick(campaign.id);
              }}
            >
              <img
                src={campaign.horizontalMediaUrl || ''}
                alt={ad.title}
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20 pointer-events-none" />
              <div className="absolute bottom-4 left-4 right-20 flex items-center gap-3 pointer-events-none">
                <span className="text-white font-bold text-base sm:text-lg drop-shadow-md line-clamp-1">
                  {ad.title}
                </span>
                {ad.redirectUrl ? (
                  <span className="bg-primary/90 backdrop-blur-md text-white text-xs px-2.5 py-1 rounded-md font-medium shadow-sm whitespace-nowrap shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    Visit Link
                  </span>
                ) : null}
              </div>
            </a>
          </motion.div>
        </AnimatePresence>

        {count > 1 ? (
          <>
            {isHoldPaused ? (
              <div className="absolute top-3 right-3 z-20 flex h-9 w-9 items-center justify-center rounded-full border border-white/20 bg-black/55 text-white shadow-lg backdrop-blur-md">
                <Pause className="h-4 w-4" />
              </div>
            ) : null}

            <div className="absolute bottom-3 right-3 z-20 flex items-center gap-2.5 rounded-full border border-white/20 bg-black/55 px-3 py-1.5 text-white shadow-lg backdrop-blur-md">
              <div className="flex items-center gap-1.5">
                {campaigns.map((c, i) => (
                  <button
                    key={c.id}
                    type="button"
                    aria-label={`Go to advertisement ${i + 1}`}
                    className={cn(
                      'rounded-full transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80',
                      i === index ? 'h-2 w-5 bg-white' : 'h-2 w-2 bg-white/40 hover:bg-white/70'
                    )}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDirection(i > index ? 1 : -1);
                      setIndex(i);
                    }}
                  />
                ))}
              </div>
              <span className="text-[11px] font-semibold tabular-nums text-white/90">
                {index + 1}/{count}
              </span>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
